import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { Add, Edit } from '@mui/icons-material';
import PlaceIcon from '@mui/icons-material/Place';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { useAuthInfo } from '@propelauth/react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { CreateOrgUserOut, OrgUserOut, UserService } from '../api';
import { CellProps, TableSortState, useTable } from '../table/useTable';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { mb } from '../common/theme/mbTokens';
import { useLanguage } from '../common/GeneralUtils';
import { getActiveLocations, getLocationName, isMultiLocation } from '../common/filter/locationGrouping';
import { ColorButton } from '../common/ui/ConfimationDialog';
import RotatingLoader from '../common/ui/RotatingLoader';
import { CreateUserDialog } from './CreateUserDialog';
import { CredentialsDialog } from './CredentialsDialog';
import { ASSIGNABLE_ROLES, formatBlockedBy, formatLastActive, userFullName, userInitials } from './userManagementUtils';

const matchesSearch = (user: OrgUserOut, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();
  return `${userFullName(user)} ${user.email}`.toLowerCase().includes(normalizedSearch);
};

const FLOATING_LOCATION_KEY = '__floating__';

const getUserLocationKey = (user: OrgUserOut): string | null =>
  user.is_floating ? FLOATING_LOCATION_KEY : user.location_id ?? null;

enum UsersSortKey {
  NAME = 'name',
  EMAIL = 'email',
  ROLE = 'role',
  LOCATION = 'location',
  STATUS = 'status',
  LAST_ACTIVE = 'last_active',
}

type LocationNameById = Record<string, string>;

const SORT_VALUE_BY_KEY: Record<
  UsersSortKey,
  (user: OrgUserOut, locationNameById: LocationNameById) => string | number
> = {
  [UsersSortKey.NAME]: (user) => userFullName(user).toLowerCase(),
  [UsersSortKey.EMAIL]: (user) => user.email.toLowerCase(),
  [UsersSortKey.ROLE]: (user) => ASSIGNABLE_ROLES.indexOf(user.role),
  [UsersSortKey.LOCATION]: (user, locationNameById) =>
    (locationNameById[getUserLocationKey(user) ?? ''] ?? '').toLowerCase(),
  [UsersSortKey.STATUS]: (user) => (user.enabled ? 0 : 1),
  [UsersSortKey.LAST_ACTIVE]: (user) => (user.last_active_at ? Date.parse(user.last_active_at) : 0),
};

const DEFAULT_SORT: TableSortState = { sortBy: UsersSortKey.STATUS, sortDirection: 'asc' };

const compareSortValues = (first: string | number, second: string | number): number =>
  typeof first === 'string' && typeof second === 'string'
    ? first.localeCompare(second)
    : Number(first) - Number(second);

const sortUsers = (
  users: OrgUserOut[],
  locationNameById: LocationNameById,
  sort: TableSortState = DEFAULT_SORT,
): OrgUserOut[] => {
  const sortValue = SORT_VALUE_BY_KEY[sort.sortBy as UsersSortKey];
  const nameValue = SORT_VALUE_BY_KEY[UsersSortKey.NAME];
  const direction = sort.sortDirection === 'asc' ? 1 : -1;
  return [...users].sort(
    (first, second) =>
      direction * compareSortValues(sortValue(first, locationNameById), sortValue(second, locationNameById)) ||
      compareSortValues(nameValue(first, locationNameById), nameValue(second, locationNameById)),
  );
};

const UsersPageContent = () => {
  const { t } = useTranslation();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const language = useLanguage();
  const navigate = useNavigate();
  const [listedUsers, setListedUsers] = useState<OrgUserOut[] | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreateOrgUserOut | undefined>(undefined);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [debouncedLocationId] = useDebounce(selectedLocationId, isSmallScreen ? 1500 : 500);

  const shouldShowLocations = !!account && isMultiLocation(account);
  const activeLocations = account ? getActiveLocations(account) : [];
  const locationNameById: LocationNameById = {
    ...Object.fromEntries(activeLocations.map((location) => [location.id, getLocationName(location, language)])),
    [FLOATING_LOCATION_KEY]: t('users.location_floating'),
  };
  const shownUsersCount = listedUsers?.filter((user) => getUserLocationKey(user) === debouncedLocationId).length ?? 0;
  const isDebouncedLocationEmpty = !!debouncedLocationId && !!listedUsers && shownUsersCount === 0;

  const usersFetcher = async ({
    search,
    sort,
    filterState,
  }: {
    search?: string;
    sort?: TableSortState;
    filterState?: { locationId?: string | null };
  }) => {
    try {
      const users = await UserService.getOrgUsers();
      const fetchedLocationId = filterState?.locationId;
      const locationUsers = fetchedLocationId
        ? users.filter((user) => getUserLocationKey(user) === fetchedLocationId)
        : users;
      const matchingUsers = search ? locationUsers.filter((user) => matchesSearch(user, search)) : locationUsers;
      setLoadFailed(false);
      setListedUsers(users);
      return sortUsers(matchingUsers, locationNameById, sort);
    } catch (error) {
      setLoadFailed(true);
      throw error;
    }
  };

  const tableDetails: CellProps<OrgUserOut>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (user: OrgUserOut) => (
        <IconButton
          className="action-icon"
          aria-label={t('users.edit_aria', { name: userFullName(user) })}
          onClick={() => navigate(`/dashboard/users/${user.user_id}`)}>
          <Edit />
        </IconButton>
      ),
    },
    {
      id: 'name',
      alignment: 'left',
      sortKey: UsersSortKey.NAME,
      label: t('users.columns.name'),
      render: (user: OrgUserOut) => (
        <Grid container gap={1.5} alignItems="center" flexWrap="nowrap">
          <Avatar variant="rounded" sx={{ width: 32, height: 32, fontSize: 14 }}>
            {userInitials(user)}
          </Avatar>
          <Link
            component={RouterLink}
            to={`/dashboard/users/${user.user_id}`}
            variant="subtitle2"
            color="inherit"
            underline="hover">
            {userFullName(user)}
          </Link>
          {user.is_current_user && <Chip size="small" label={t('users.you')} />}
        </Grid>
      ),
    },
    {
      id: 'email',
      alignment: 'left',
      sortKey: UsersSortKey.EMAIL,
      label: t('users.columns.email'),
      render: (user: OrgUserOut) => (
        <Typography variant="body2" dir="ltr" sx={{ textTransform: 'none' }}>
          {user.email}
        </Typography>
      ),
    },
    {
      id: 'role',
      alignment: 'left',
      sortKey: UsersSortKey.ROLE,
      label: t('users.columns.role'),
      render: (user: OrgUserOut) => t(`users.roles.${user.role}`),
    },
    ...(shouldShowLocations
      ? [
          {
            id: 'location',
            alignment: 'left' as const,
            sortKey: UsersSortKey.LOCATION,
            label: t('users.columns.location'),
            render: (user: OrgUserOut) => {
              const locationKey = getUserLocationKey(user);
              return locationKey && locationNameById[locationKey] ? (
                <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                  <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{locationNameById[locationKey]}</Typography>
                </Grid>
              ) : (
                '—'
              );
            },
          },
        ]
      : []),
    {
      id: 'status',
      alignment: 'left',
      sortKey: UsersSortKey.STATUS,
      label: t('users.columns.status'),
      render: (user: OrgUserOut) =>
        user.enabled ? (
          <Chip size="small" color="success" label={t('users.status.active')} />
        ) : (
          <Tooltip title={formatBlockedBy(t, user) ?? ''}>
            <Chip size="small" label={t('users.status.blocked')} />
          </Tooltip>
        ),
    },
    {
      id: 'last_active',
      alignment: 'left',
      sortKey: UsersSortKey.LAST_ACTIVE,
      label: t('users.columns.last_active'),
      render: (user: OrgUserOut) =>
        user.last_active_at ? (
          formatLastActive(user.last_active_at)
        ) : (
          <Chip size="small" color="warning" label={t('users.never_logged_in')} />
        ),
    },
  ];

  const { mutate, TableComponent } = useTable<OrgUserOut>({
    opId: 'getOrgUsers',
    fetcher: usersFetcher,
    tableDetails,
    hidePaging: true,
    defaultRowsPerPage: 500,
    fillHeight: !isDebouncedLocationEmpty,
    filterState: { locationId: selectedLocationId },
    getRowSx: (user: OrgUserOut) => (user.enabled ? undefined : { opacity: 0.55 }),
    actionsComponent: listedUsers && (
      <Grid container alignItems="center" gap={1.5}>
        {shouldShowLocations && (
          <TextField
            select
            size="small"
            value={selectedLocationId ?? ''}
            onChange={(event) => setSelectedLocationId(event.target.value || null)}
            sx={{ width: 220 }}
            SelectProps={{
              displayEmpty: true,
              SelectDisplayProps: { 'aria-label': t('users.filter.location_aria') },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}>
            <MenuItem value="">{t('users.filter.all_locations')}</MenuItem>
            {activeLocations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {locationNameById[location.id]}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value={FLOATING_LOCATION_KEY}>{t('users.location_floating')}</MenuItem>
          </TextField>
        )}
        <Typography variant="caption" color="text.secondary">
          {debouncedLocationId
            ? t('users.filter.summary_filtered', { shown: shownUsersCount, total: listedUsers.length })
            : t('users.summary', {
                total: listedUsers.length,
                blocked: listedUsers.filter((user) => !user.enabled).length,
              })}
        </Typography>
      </Grid>
    ),
  });

  const refreshAfterWrite = () => {
    void mutate();
    void updateLocalAccount();
  };

  const handleCreated = (result: CreateOrgUserOut) => {
    setIsCreateDialogOpen(false);
    setCreatedUser(result);
    refreshAfterWrite();
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{ px: mb.layout.cardPadding, pt: mb.layout.cardPadding }}>
        <Grid item>
          <Typography variant="h5">{t('users.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('users.subtitle')}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={() => setIsCreateDialogOpen(true)}>
            {t('users.add_user')}
          </Button>
        </Grid>
      </Grid>
      {loadFailed ? (
        <Grid container sx={{ p: mb.layout.cardPadding }}>
          <Alert
            severity="error"
            sx={{ width: '100%' }}
            action={
              <Button color="inherit" size="small" onClick={() => mutate()}>
                {t('users.try_again')}
              </Button>
            }>
            {t('users.load_error')}
          </Alert>
        </Grid>
      ) : (
        <>
          {TableComponent}
          {isDebouncedLocationEmpty && (
            <Grid container flexDirection="column" alignItems="center" gap={1.5} sx={{ pt: 6, pb: 7, px: 2 }}>
              <Typography variant="subtitle2">{t('users.filter.empty_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('users.filter.empty_body')}
              </Typography>
              <ColorButton size="small" sx={{ textTransform: 'none' }} onClick={() => setSelectedLocationId(null)}>
                {t('users.filter.show_all')}
              </ColorButton>
            </Grid>
          )}
        </>
      )}
      {isCreateDialogOpen && (
        <CreateUserDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {createdUser && (
        <CredentialsDialog
          isOpen
          onClose={() => setCreatedUser(undefined)}
          user={createdUser.user}
          credentials={createdUser.credentials}
          isNewUser
        />
      )}
    </Grid>
  );
};

export const UsersPage = () => {
  const { loading } = useAuthInfo();
  const { isAdmin } = UseAuthPermissions();

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="200px">
        <RotatingLoader />
      </Grid>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard/calendar" replace />;
  }

  return <UsersPageContent />;
};
